import { useEffect, useState } from 'react';
import { X, Save, Pencil, Sparkles } from 'lucide-react';
import { adminBooksApi } from '../../services/adminApi';

const EMPTY_FORM = {
  illustrator: '',
  cover_url: '',
  number_of_pages: '',
  reading_age: '',
  publisher: '',
  publication_date: '',
  description: '',
};

function normalizeFormData(book) {
  return {
    illustrator: book.illustrator || '',
    cover_url: book.cover_url || '',
    number_of_pages: book.number_of_pages ?? '',
    reading_age: book.reading_age || '',
    publisher: book.publisher || '',
    publication_date: book.publication_date ? String(book.publication_date).slice(0, 10) : '',
    description: book.description || '',
  };
}

function normalizeTagList(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }

  if (typeof value === 'string') {
    try {
      return normalizeTagList(JSON.parse(value));
    } catch (_error) {
      return value.trim() ? [value.trim()] : [];
    }
  }

  if (typeof value === 'object') {
    return Object.values(value)
      .flatMap((entry) => (Array.isArray(entry) ? entry : []))
      .map(String)
      .filter(Boolean);
  }

  return [];
}

const TagList = ({ tags, emptyText }) => {
  const normalizedTags = normalizeTagList(tags);

  if (normalizedTags.length === 0) {
    return <p className="text-sm text-text-secondary">{emptyText}</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {normalizedTags.map((tag) => (
        <span key={tag} className="rounded-full bg-white px-2 py-1 text-xs font-medium text-text-primary border border-gray-200">
          {tag}
        </span>
      ))}
    </div>
  );
};

const BookUpdateFieldsModal = ({ isOpen, bookId, onClose, onSuccess }) => {
  const [book, setBook] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingTags, setGeneratingTags] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [tagPreview, setTagPreview] = useState(null);
  const [adminTagInput, setAdminTagInput] = useState('');
  const [tagMessage, setTagMessage] = useState(null);
  const [tagError, setTagError] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !bookId) {
      setBook(null);
      setFormData(EMPTY_FORM);
      setAdminTagInput('');
      setTagMessage(null);
      setTagError(null);
      setTagPreview(null);
      setError(null);
      setLoading(false);
      setSaving(false);
      setGeneratingTags(false);
      setSavingTags(false);
      return;
    }

    let cancelled = false;

    const loadBook = async () => {
      try {
        setLoading(true);
        setError(null);
        setTagMessage(null);
        setTagError(null);
        setTagPreview(null);
        const response = await adminBooksApi.getBookDetail(bookId);
        if (cancelled) return;

        setBook(response.book);
        setFormData(normalizeFormData(response.book));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load book fields');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadBook();

    return () => {
      cancelled = true;
    };
  }, [isOpen, bookId]);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!bookId) return;

    const payload = {
      illustrator: formData.illustrator,
      cover_url: formData.cover_url,
      number_of_pages: formData.number_of_pages === '' ? null : Number(formData.number_of_pages),
      reading_age: formData.reading_age,
      publisher: formData.publisher,
      publication_date: formData.publication_date || null,
      description: formData.description,
    };

    try {
      setSaving(true);
      setError(null);
      const response = await adminBooksApi.updateBookFields(bookId, payload);
      setBook(response.book);
      setFormData(normalizeFormData(response.book));
      onSuccess?.(response.book, response.message || 'Book fields updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update book fields');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!bookId) return;

    const hasDescription = Boolean(String(formData.description || '').trim());
    const hasAdminInput = Boolean(adminTagInput.trim());

    if (!hasDescription && !hasAdminInput) {
      setTagMessage(null);
      setTagError('Tag generation requires either a book description or admin input text.');
      return;
    }

    try {
      setGeneratingTags(true);
      setTagMessage(null);
      setTagError(null);

      const response = await adminBooksApi.previewBookTags(bookId, {
        admin_input_text: adminTagInput,
        description_override: formData.description,
      });

      setTagPreview(response);
      if (response.fallback_used) {
        setTagMessage(null);
        setTagError('AI tag generation returned a safe fallback. Review the input and generate another preview before saving.');
      } else {
        setTagMessage(response.message || 'AI tag preview generated successfully.');
      }
    } catch (err) {
      setTagPreview(null);
      setTagError(err.message || 'Failed to generate AI tag preview');
    } finally {
      setGeneratingTags(false);
    }
  };

  const handleSaveTags = async () => {
    if (!bookId || !tagPreview?.proposed?.ai_tags_v2 || tagPreview.fallback_used) return;

    try {
      setSavingTags(true);
      setTagMessage(null);
      setTagError(null);

      const response = await adminBooksApi.saveBookTags(bookId, {
        ai_tags_v2: tagPreview.proposed.ai_tags_v2,
        ai_tags_display: tagPreview.proposed.ai_tags_display,
      });

      setBook((previous) => ({
        ...previous,
        ai_tags_v2: response.ai_tags_v2,
        ai_tags_display: response.ai_tags_display,
      }));
      setAdminTagInput('');
      setTagPreview(null);
      setTagMessage(response.message || 'AI tags saved successfully.');
      onSuccess?.(response.book, response.message || 'AI tags saved successfully.', { closeModal: false, tagsOnly: true });
    } catch (err) {
      setTagError(err.message || 'Failed to save AI tags');
    } finally {
      setSavingTags(false);
    }
  };

  const handleClose = () => {
    if (saving || generatingTags || savingTags) return;
    onClose();
  };

  const readOnlyRows = [
    { label: 'Title', value: book?.title || 'Unknown title' },
    { label: 'Author', value: book?.author || 'Unknown author' },
    { label: 'ISBN-13', value: book?.isbn13 || 'Not available' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <div className="flex items-center space-x-2">
            <Pencil className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-text-primary">Update Book Fields</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={saving || generatingTags || savingTags}
            className="rounded-full p-2 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="text-sm font-medium text-text-primary">Read-only metadata</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              {readOnlyRows.map((row) => (
                <div key={row.label}>
                  <div className="text-xs uppercase tracking-wide text-text-secondary">{row.label}</div>
                  <div className="mt-1 text-sm text-text-primary break-words">{row.value}</div>
                </div>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center">
              <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
              <p className="text-sm text-text-secondary">Loading book details...</p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Illustrator</label>
                  <input
                    type="text"
                    value={formData.illustrator}
                    onChange={(event) => handleInputChange('illustrator', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Publisher</label>
                  <input
                    type="text"
                    value={formData.publisher}
                    onChange={(event) => handleInputChange('publisher', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Number of Pages</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.number_of_pages}
                    onChange={(event) => handleInputChange('number_of_pages', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Publication Date</label>
                  <input
                    type="date"
                    value={formData.publication_date}
                    onChange={(event) => handleInputChange('publication_date', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Reading Age</label>
                  <input
                    type="text"
                    value={formData.reading_age}
                    onChange={(event) => handleInputChange('reading_age', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Cover URL</label>
                  <input
                    type="url"
                    value={formData.cover_url}
                    onChange={(event) => handleInputChange('cover_url', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    disabled={saving}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(event) => handleInputChange('description', event.target.value)}
                  className="h-32 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  disabled={saving}
                />
              </div>

              <div className="rounded-lg border border-primary-100 bg-primary-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary-700" />
                      <h3 className="text-sm font-semibold text-text-primary">Generate AI Tags</h3>
                    </div>
                    <p className="mt-2 text-sm text-text-secondary">
                      Leave blank to use the book description only. Add text here if you want to guide the tag generation.
                    </p>
                    <p className="mt-1 text-xs text-amber-700">
                      Save Tags will replace the current AI tags for this book.
                    </p>
                    <p className="mt-1 text-xs text-text-secondary">
                      Save Changes updates book fields only. Save Tags updates AI tags only.
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-primary-100 bg-white p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Current saved tags</div>
                  <TagList tags={tagPreview?.current?.ai_tags_display ?? book?.ai_tags_display} emptyText="No AI tags saved yet." />
                </div>

                <textarea
                  value={adminTagInput}
                  onChange={(event) => setAdminTagInput(event.target.value)}
                  className="mt-3 h-24 w-full rounded-lg border border-primary-100 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                  placeholder="Optional context for tag generation..."
                  disabled={saving || generatingTags || savingTags}
                />

                {tagPreview && (
                  <div className="mt-4 grid gap-3">
                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Proposed new display tags</div>
                      <TagList tags={tagPreview.proposed?.ai_tags_display} emptyText="No proposed display tags." />
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Proposed ai_tags_v2</div>
                      <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs text-text-primary">
                        {JSON.stringify(tagPreview.proposed?.ai_tags_v2 || {}, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {tagError && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {tagError}
                  </div>
                )}

                {tagMessage && (
                  <div className="mt-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                    {tagMessage}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleGeneratePreview}
                    disabled={loading || saving || generatingTags || savingTags}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    {generatingTags ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    <span>{generatingTags ? 'Generating...' : 'Generate Preview'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTags}
                    disabled={loading || saving || generatingTags || savingTags || !tagPreview?.proposed?.ai_tags_v2 || tagPreview?.fallback_used}
                    className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {savingTags ? 'Saving Tags...' : 'Save Tags'}
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="sticky bottom-0 -mx-6 flex justify-end space-x-3 border-t border-gray-200 bg-white px-6 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving || generatingTags || savingTags}
              className="rounded border border-gray-200 px-4 py-2 text-text-secondary hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || saving || generatingTags || savingTags}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              {saving ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookUpdateFieldsModal;
